import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import { api, fmtPKR } from "../lib/api";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import StatusPill from "../components/StatusPill";
import { Progress } from "../components/ui/progress";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

function DailyTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/daily", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  return (
    <div className="space-y-5" data-testid="daily-report">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Range</label>
          <div className="flex gap-1.5">
            {[7, 14, 30, 60, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                  days === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"
                }`}
                data-testid={`daily-range-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        {data && (
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total</div>
            <div className="font-display text-3xl font-bold text-emerald-700 tabular">{fmtPKR(data.total)}</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="font-display font-semibold text-lg mb-3">Daily collections</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.rows || []}>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip formatter={(v) => fmtPKR(v)} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0" }} />
              <Line type="monotone" dataKey="amount" stroke="#059669" strokeWidth={2} dot={{ r: 3, fill: "#059669" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-right px-4 py-3 font-semibold">Entries</th>
              <th className="text-right px-4 py-3 font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.date} className="border-t border-slate-100" data-testid={`daily-row-${r.date}`}>
                <td className="px-4 py-2 tabular">{r.date}</td>
                <td className="px-4 py-2 text-right tabular">{r.count}</td>
                <td className="px-4 py-2 text-right tabular font-semibold">{fmtPKR(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthlyTab() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/monthly", { params: { months } }).then((r) => setData(r.data));
  }, [months]);

  return (
    <div className="space-y-5" data-testid="monthly-report">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Months</label>
          <div className="flex gap-1.5">
            {[3, 6, 12].map((d) => (
              <button
                key={d}
                onClick={() => setMonths(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                  months === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"
                }`}
                data-testid={`monthly-range-${d}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        {data && (
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total</div>
            <div className="font-display text-3xl font-bold text-emerald-700 tabular">{fmtPKR(data.total)}</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="font-display font-semibold text-lg mb-3">Monthly collections</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.rows || []}>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip formatter={(v) => fmtPKR(v)} contentStyle={{ borderRadius: 6, borderColor: "#e2e8f0" }} />
              <Bar dataKey="amount" fill="#059669" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Month</th>
              <th className="text-right px-4 py-3 font-semibold">Entries</th>
              <th className="text-right px-4 py-3 font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.month} className="border-t border-slate-100" data-testid={`monthly-row-${r.month}`}>
                <td className="px-4 py-2 tabular">{r.month}</td>
                <td className="px-4 py-2 text-right tabular">{r.count}</td>
                <td className="px-4 py-2 text-right tabular font-semibold">{fmtPKR(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OutstandingTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/outstanding").then((r) => setData(r.data));
  }, []);
  return (
    <div className="space-y-5" data-testid="outstanding-report">
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Active loans</div>
            <div className="font-display text-2xl font-bold mt-1 tabular">{data.count}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5 sm:col-span-2">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total outstanding</div>
            <div className="font-display text-3xl font-bold text-emerald-700 mt-1 tabular">{fmtPKR(data.total)}</div>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Customer</th>
              <th className="text-left px-4 py-3 font-semibold">Product</th>
              <th className="text-right px-4 py-3 font-semibold">Paid</th>
              <th className="text-right px-4 py-3 font-semibold">Remaining</th>
              <th className="text-left px-4 py-3 font-semibold w-40">Progress</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => {
              const pct = r.financed_amount > 0 ? Math.min(100, (r.paid_amount / r.financed_amount) * 100) : 0;
              return (
                <tr key={r.loan_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`out-row-${r.loan_id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${r.customer_id}`} className="font-medium hover:text-emerald-700">{r.customer_name}</Link>
                    <div className="text-xs text-slate-500 tabular">{r.customer_mobile}</div>
                  </td>
                  <td className="px-4 py-3">{r.product_name}</td>
                  <td className="px-4 py-3 text-right tabular">{fmtPKR(r.paid_amount)}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{fmtPKR(r.remaining_amount)}</td>
                  <td className="px-4 py-3">
                    <Progress value={pct} className="h-1.5 [&>div]:bg-emerald-600" />
                    <div className="text-xs text-slate-500 tabular mt-0.5">{pct.toFixed(0)}%</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={r.current_status} daysLate={r.days_late} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DefaulterTab() {
  const [minDays, setMinDays] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/defaulters", { params: { min_days: minDays } }).then((r) => setData(r.data));
  }, [minDays]);

  return (
    <div className="space-y-5" data-testid="defaulter-report">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-slate-600">Minimum days late</label>
          <div className="flex gap-1.5">
            {[1, 7, 15, 30, 60].map((d) => (
              <button
                key={d}
                onClick={() => setMinDays(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                  minDays === d ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"
                }`}
                data-testid={`default-min-${d}`}
              >
                {d}+
              </button>
            ))}
          </div>
        </div>
        {data && (
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Defaulters</div>
            <div className="font-display text-3xl font-bold text-red-600 tabular">{data.count}</div>
            <div className="text-xs tabular text-slate-600">{fmtPKR(data.total)} outstanding</div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">#</th>
              <th className="text-left px-4 py-3 font-semibold">Customer</th>
              <th className="text-left px-4 py-3 font-semibold">Product</th>
              <th className="text-right px-4 py-3 font-semibold">Days late</th>
              <th className="text-right px-4 py-3 font-semibold">Remaining</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No defaulters in this window.</td></tr>
            )}
            {(data?.rows || []).map((r, i) => (
              <tr key={r.loan_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`def-row-${r.loan_id}`}>
                <td className="px-4 py-3 tabular text-slate-500">{i + 1}</td>
                <td className="px-4 py-3">
                  <Link to={`/customers/${r.customer_id}`} className="font-medium hover:text-emerald-700">{r.customer_name}</Link>
                  <div className="text-xs text-slate-500 tabular">{r.customer_mobile}</div>
                </td>
                <td className="px-4 py-3">{r.product_name}</td>
                <td className="px-4 py-3 text-right tabular font-semibold text-red-600">{r.days_late}d</td>
                <td className="px-4 py-3 text-right tabular">{fmtPKR(r.remaining_amount)}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/customers/${r.customer_id}`} className="text-emerald-700 font-semibold text-sm">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Reports</h1>
        <p className="text-slate-500 mt-1">Collections, outstanding, and defaulters at a glance.</p>
        <Link to="/reports/profit" className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-emerald-700 hover:text-emerald-800 hover:underline" data-testid="reports-profit-link">
          <TrendingUp className="w-4 h-4" /> Open Profit Report (owner only) →
        </Link>
      </div>

      <Tabs defaultValue="daily" className="space-y-5">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="tab-outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="defaulters" data-testid="tab-defaulters">Defaulters</TabsTrigger>
        </TabsList>
        <TabsContent value="daily"><DailyTab /></TabsContent>
        <TabsContent value="monthly"><MonthlyTab /></TabsContent>
        <TabsContent value="outstanding"><OutstandingTab /></TabsContent>
        <TabsContent value="defaulters"><DefaulterTab /></TabsContent>
      </Tabs>
    </div>
  );
}
